import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { listOperations } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { OperationListFilter } from '../../api/types';
import { FlowisAvailabilityBanner } from '../../components/FlowisAvailabilityBanner';
import { ErrorBlock } from '../../components/QueryStateOverlay';
import { OperationStatusChip } from '../../components/StatusChips';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { formatDateTime } from '../../utils/format';
import { SendDialog } from '../operations/SendDialog';

/** The caller's own submissions, newest first — the server narrows a submitter to these anyway. */
const mySubmissionsFilter: OperationListFilter = {
  page: 1,
  pageSize: 10,
  mine: true,
  sortBy: 'submittedAt',
  sortDirection: 'desc',
};

/**
 * The submitter's whole console: a bare `registration` role reaches the one-call submit, the
 * operation details it leads to, and — through the author-narrowed listing — their own past
 * submissions. This page offers exactly that and the navigation shows nothing more. Full users are
 * never sent here; they have the same dialog on Operations.
 */
export function SubmitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedTenantGid } = useSelectedTenant();
  const noTenantKey = useNoTenantMessageKey();
  const hasNoTenant = selectedTenantGid === null;

  const [sendOpen, setSendOpen] = useState(false);

  const recentQuery = useQuery({
    queryKey: queryKeys.operations.list(selectedTenantGid, mySubmissionsFilter),
    queryFn: ({ signal }) => listOperations(mySubmissionsFilter, signal),
    enabled: selectedTenantGid !== null,
  });

  const recent = recentQuery.data?.items ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">{t('submit.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('submit.description')}
        </Typography>
      </Box>

      <FlowisAvailabilityBanner />

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            {t('submit.hint')}
          </Typography>
          <Tooltip title={hasNoTenant ? t(noTenantKey) : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                disabled={hasNoTenant}
                onClick={() => setSendOpen(true)}
              >
                {t('submit.action')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {!hasNoTenant && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('submit.recentTitle')}
          </Typography>
          {recentQuery.isError ? (
            <ErrorBlock error={recentQuery.error} onRetry={() => void recentQuery.refetch()} />
          ) : recent.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('submit.recentEmpty')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('operations.columns.status')}</TableCell>
                  <TableCell>{t('operations.columns.document')}</TableCell>
                  <TableCell>{t('operations.columns.flowisStatus')}</TableCell>
                  <TableCell>{t('operations.columns.submitted')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((operation) => (
                  <TableRow
                    key={operation.operationGid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => void navigate(`/operations/${operation.operationGid}`)}
                  >
                    <TableCell>
                      <OperationStatusChip status={operation.status} />
                    </TableCell>
                    <TableCell>{operation.documentFileName}</TableCell>
                    <TableCell>{operation.flowisStatus ?? '—'}</TableCell>
                    <TableCell>{formatDateTime(operation.submittedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {sendOpen && <SendDialog uploadOnly onClose={() => setSendOpen(false)} />}
    </Stack>
  );
}
